import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { Trash2, ArrowLeft, Power, X, Library, ArrowUp, ArrowDown, Pencil } from "lucide-react";
import QuestionForm from "../../components/QuestionForm";

export default function AdminQuestions() {
  const { quizId } = useParams();
  const [questions, setQuestions] = useState([]);
  const [editing, setEditing] = useState(null); // question object being edited, or null (=create new)
  const [pickOpen, setPickOpen] = useState(false);
  const [archive, setArchive] = useState([]);
  const [pickSearch, setPickSearch] = useState("");
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    const { data } = await api.get(`/admin/quizzes/${quizId}/questions`);
    setQuestions(data || []);
  };
  useEffect(() => { load(); }, [quizId]);

  const handleSave = async (form) => {
    try {
      if (editing) {
        await api.put(`/admin/questions/${editing.id}`, form);
        toast.success("Pergunta atualizada");
      } else {
        await api.post(`/admin/quizzes/${quizId}/questions`, form);
        toast.success("Pergunta criada e adicionada ao arquivo");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
      throw e;
    }
  };

  const toggleActive = async (q) => {
    try { await api.put(`/admin/questions/${q.id}`, { active: q.active === false }); load(); }
    catch { toast.error("Não foi possível alterar o estado"); }
  };

  const removeFromQuiz = async (id) => {
    if (!window.confirm("Remover esta pergunta deste quiz? Ela continua no Arquivo de Perguntas.")) return;
    await api.post(`/admin/quizzes/${quizId}/questions/${id}/remove`);
    toast.success("Removida deste quiz");
    if (editing?.id === id) setEditing(null);
    load();
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= questions.length) return;
    const arr = [...questions];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setQuestions(arr);
    await api.put(`/admin/quizzes/${quizId}/questions/reorder`, { ids: arr.map((q) => q.id) });
  };

  const openPicker = async () => {
    try {
      const { data } = await api.get(`/admin/questions`);
      const existing = new Set(questions.map((q) => q.id));
      setArchive((data || []).filter((q) => !existing.has(q.id)));
      setPickSearch("");
      setPickOpen(true);
    } catch { toast.error("Não foi possível carregar o arquivo"); }
  };

  const addExisting = async (qid) => {
    try {
      await api.post(`/admin/quizzes/${quizId}/questions/add-existing`, { question_id: qid });
      toast.success("Pergunta adicionada ao quiz");
      setPickOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao adicionar"); }
  };

  const filteredArchive = archive.filter((q) => {
    const s = pickSearch.trim().toLowerCase();
    if (!s) return true;
    return (q.identification || "").toLowerCase().includes(s) || (q.question_pt || "").toLowerCase().includes(s);
  });

  return (
    <div data-testid="admin-questions-page">
      <Link to="/admin/quizzes" className="inline-flex items-center gap-1 text-sm text-[#0B1D3A]/60 mb-4"><ArrowLeft size={14}/> Voltar</Link>
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <h1 className="font-outfit text-3xl font-bold text-[#0B1D3A]">Perguntas do Quiz</h1>
        <button type="button" onClick={openPicker} data-testid="pick-existing-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#005F73] text-white text-sm font-bold hover:bg-[#024a59] transition-colors">
          <Library size={15} /> Inserir pergunta do arquivo
        </button>
      </div>

      {editing && (
        <div className="flex items-center justify-between rounded-lg bg-[#FFF6E5] border border-[#FFD93D] px-4 py-2 mb-3 text-sm">
          <span className="font-semibold text-[#0B1D3A]">A editar: {editing.identification || editing.question_pt}</span>
          <button onClick={() => setEditing(null)} className="text-[#0B1D3A]/60 inline-flex items-center gap-1" data-testid="cancel-edit-btn"><X size={14}/> Cancelar edição</button>
        </div>
      )}

      <QuestionForm
        key={editing ? editing.id : "new"}
        initial={editing}
        onSubmit={handleSave}
        submitLabel={editing ? "Atualizar pergunta" : "Adicionar pergunta"}
      />

      <div className="space-y-3">
        {questions.map((q, idx) => {
          const isActive = q.active !== false;
          return (
          <div key={q.id} className={`rounded-xl bg-white p-4 border flex items-center gap-3 ${isActive ? "border-[#0B1D3A]/5" : "border-[#0B1D3A]/10 opacity-60"}`} data-testid={`question-row-${q.id}`}>
            <div className="flex flex-col gap-1">
              <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-[#0B1D3A]/40 disabled:opacity-20" data-testid={`q-up-${q.id}`}><ArrowUp size={15}/></button>
              <button onClick={() => move(idx, 1)} disabled={idx === questions.length - 1} className="text-[#0B1D3A]/40 disabled:opacity-20" data-testid={`q-down-${q.id}`}><ArrowDown size={15}/></button>
            </div>
            {q.image_path && <img src={`${backendUrl}/api/files/${q.image_path}`} alt="" className="h-14 w-14 object-cover rounded-lg" />}
            <div className="flex-1 min-w-0">
              {q.identification && <p className="text-[11px] font-bold text-[#005F73] uppercase tracking-wide truncate">{q.identification}</p>}
              <p className="font-semibold text-[#0B1D3A] truncate">{idx + 1}. {q.question_pt}</p>
              <p className="text-xs text-[#0B1D3A]/60">Correta: {String.fromCharCode(65 + q.correct_index)} • Penalização {q.penalty_seconds}s</p>
            </div>
            <button onClick={() => toggleActive(q)} data-testid={`question-toggle-active-${q.id}`}
              className={`text-xs px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 font-bold transition-colors ${isActive ? "bg-[#2D9C5A]/10 text-[#2D9C5A] hover:bg-[#2D9C5A]/20" : "bg-[#0B1D3A]/5 text-[#0B1D3A]/50 hover:bg-[#0B1D3A]/10"}`}>
              <Power size={13} /> {isActive ? "Ativa" : "Inativa"}
            </button>
            <button onClick={() => { setEditing(q); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="text-xs px-3 py-1.5 rounded-md bg-[#0B1D3A]/5 inline-flex items-center gap-1" data-testid={`question-edit-${q.id}`}><Pencil size={13}/> Editar</button>
            <button onClick={() => removeFromQuiz(q.id)} className="text-xs px-3 py-1.5 rounded-md bg-red-50 text-red-600 inline-flex items-center gap-1" data-testid={`question-remove-${q.id}`} title="Remover deste quiz"><Trash2 size={14} /></button>
          </div>
          );
        })}
        {questions.length === 0 && <p className="text-sm text-[#0B1D3A]/50">Sem perguntas ainda.</p>}
      </div>

      {pickOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0B1D3A]/60 backdrop-blur-sm p-4" data-testid="pick-question-modal" onClick={() => setPickOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#0B1D3A]/10 sticky top-0 bg-white">
              <h3 className="font-outfit font-bold text-lg text-[#0B1D3A]">Inserir pergunta do arquivo</h3>
              <button onClick={() => setPickOpen(false)} data-testid="pick-close-btn" className="p-1.5 rounded-lg hover:bg-[#0B1D3A]/5"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <input value={pickSearch} onChange={(e) => setPickSearch(e.target.value)} placeholder="Pesquisar por identificação ou pergunta..." className="w-full px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm" data-testid="pick-search-input" />
              {filteredArchive.length === 0 && <p className="text-sm text-[#0B1D3A]/50">Nenhuma pergunta disponível no arquivo.</p>}
              {filteredArchive.map((sq) => (
                <div key={sq.id} className="flex items-center gap-3 rounded-lg border border-[#0B1D3A]/10 p-2.5" data-testid={`pick-source-${sq.id}`}>
                  {sq.image_path && <img src={`${backendUrl}/api/files/${sq.image_path}`} alt="" className="h-10 w-10 object-cover rounded-md" />}
                  <div className="flex-1 min-w-0">
                    {sq.identification && <p className="text-[11px] font-bold text-[#005F73] uppercase truncate">{sq.identification}</p>}
                    <p className="text-sm text-[#0B1D3A] truncate">{sq.question_pt}</p>
                  </div>
                  <button onClick={() => addExisting(sq.id)} data-testid={`pick-add-${sq.id}`}
                    className="shrink-0 text-xs px-3 py-1.5 rounded-md bg-[#005F73] text-white font-bold hover:bg-[#024a59]">Inserir</button>
                </div>
              ))}
              <p className="text-[11px] text-[#0B1D3A]/45">A pergunta é <b>partilhada</b>: editá-la (aqui ou no Arquivo) reflete-se em todos os quizzes onde está.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
